import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import * as React from "react";
import {
  DayButton,
  DayFlag,
  DayPicker,
  getDefaultClassNames,
  SelectionState,
  UI,
  useDayPicker,
} from "react-day-picker";
import type { DropdownProps } from "react-day-picker";

import { Button } from "../../primitives/button";
import { cn } from "../../utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

function CalendarDropdown({
  options,
  value,
  onChange,
  className,
  disabled,
  ...props
}: DropdownProps) {
  const { classNames } = useDayPicker();
  const selectedOption = options?.find(
    (option) => String(option.value) === String(value),
  );

  return (
    <Select
      value={value === undefined ? undefined : String(value)}
      onValueChange={(nextValue) => {
        onChange?.({
          currentTarget: { value: nextValue },
          target: { value: nextValue },
        } as React.ChangeEvent<HTMLSelectElement>);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        aria-label={props["aria-label"]}
        className={cn(
          "h-8 min-w-20 rounded-xs px-2 text-sm shadow-none",
          classNames[UI.Dropdown],
          className,
        )}
        data-calendar-dropdown="true"
      >
        <SelectValue>{selectedOption?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options?.map((option) => (
          <SelectItem
            key={option.value}
            value={String(option.value)}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();
  const mergeClassName = (
    name: UI | DayFlag | SelectionState,
    ...values: Array<string | undefined>
  ) => cn(...values, defaultClassNames[name], classNames?.[name]);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        ...classNames,
        [UI.Root]: mergeClassName(UI.Root, "w-fit"),
        [UI.Months]: mergeClassName(
          UI.Months,
          "relative flex flex-col gap-4 md:flex-row",
        ),
        [UI.Month]: mergeClassName(UI.Month, "flex w-full flex-col gap-4"),
        [UI.Nav]: mergeClassName(
          UI.Nav,
          "pointer-events-none absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
        ),
        [UI.PreviousMonthButton]: mergeClassName(
          UI.PreviousMonthButton,
          "pointer-events-auto h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          "size-8 flex items-center justify-center rounded-xxs",
          "bg-transparent text-foreground hover:bg-accent active:bg-accent disabled:text-muted-foreground",
        ),
        [UI.NextMonthButton]: mergeClassName(
          UI.NextMonthButton,
          "pointer-events-auto h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          "size-8 flex items-center justify-center rounded-xxs",
          "bg-transparent text-foreground hover:bg-accent active:bg-accent disabled:text-muted-foreground",
        ),
        [UI.MonthCaption]: mergeClassName(
          UI.MonthCaption,
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
        ),
        [UI.Dropdowns]: mergeClassName(
          UI.Dropdowns,
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
        ),
        [UI.DropdownRoot]: mergeClassName(
          UI.DropdownRoot,
          "has-focus:border-ring border-input has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-xs border",
        ),
        [UI.Dropdown]: mergeClassName(
          UI.Dropdown,
          "bg-popover text-popover-foreground",
        ),
        [UI.CaptionLabel]: mergeClassName(
          UI.CaptionLabel,
          "select-none font-medium",
          captionLayout === "label"
            ? "text-sm"
            : "[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-xs pl-2 pr-1 text-sm [&>svg]:size-3.5",
        ),
        [UI.MonthGrid]: mergeClassName(UI.MonthGrid, "w-full border-collapse"),
        [UI.Weekdays]: mergeClassName(UI.Weekdays, "flex"),
        [UI.Weekday]: mergeClassName(
          UI.Weekday,
          "text-muted-foreground flex-1 select-none rounded-xs text-[0.8rem] font-normal",
        ),
        [UI.Week]: mergeClassName(UI.Week, "mt-2 flex w-full"),
        [UI.WeekNumberHeader]: mergeClassName(
          UI.WeekNumberHeader,
          "w-(--cell-size) select-none",
        ),
        [UI.WeekNumber]: mergeClassName(
          UI.WeekNumber,
          "text-muted-foreground select-none text-[0.8rem]",
        ),
        [UI.Day]: mergeClassName(
          UI.Day,
          "group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-xs [&:last-child[data-selected=true]_button]:rounded-r-xs",
        ),
        [SelectionState.range_start]: mergeClassName(
          SelectionState.range_start,
          "bg-accent rounded-l-xs",
        ),
        [SelectionState.range_middle]: mergeClassName(
          SelectionState.range_middle,
          "rounded-none",
        ),
        [SelectionState.range_end]: mergeClassName(
          SelectionState.range_end,
          "bg-accent rounded-r-xs",
        ),
        [DayFlag.today]: mergeClassName(
          DayFlag.today,
          "bg-accent text-accent-foreground rounded-xs data-[selected=true]:rounded-none",
        ),
        [DayFlag.outside]: mergeClassName(
          DayFlag.outside,
          "text-muted-foreground aria-selected:text-muted-foreground",
        ),
        [DayFlag.disabled]: mergeClassName(
          DayFlag.disabled,
          "text-muted-foreground opacity-50",
        ),
        [DayFlag.hidden]: mergeClassName(DayFlag.hidden, "invisible"),
        [UI.DayButton]: mergeClassName(UI.DayButton),
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return (
              <ChevronLeftIcon className={cn("size-4", className)} {...props} />
            );
          }

          if (orientation === "right") {
            return (
              <ChevronRightIcon
                className={cn("size-4", className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon className={cn("size-4", className)} {...props} />
          );
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        Dropdown: CalendarDropdown,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const selected =
    modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle;

  return (
    <Button
      ref={ref}
      variant="text"
      size="xs"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={selected}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex aspect-square h-auto w-full min-w-(--cell-size) flex-col gap-1 font-normal leading-none data-[range-end=true]:rounded-xs data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-xs group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] [&>span]:text-xs [&>span]:opacity-70",
        defaultClassNames.day,
        selected && "bg-primary text-primary-foreground",
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
